import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { generateSalesDescription } from '../../lib/openai';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCar, FiLoader, FiCopy, FiCheck } = FiIcons;

function VehicleDescriptionGenerator({ vehicle }) {
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateDescription = async () => {
    setIsLoading(true);
    try {
      const vehicleDetails = `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.type}`;
      const result = await generateSalesDescription(vehicleDetails);
      setDescription(result);
    } catch (error) {
      console.error('Error generating vehicle description:', error);
      setDescription('Failed to generate description. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(description);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <SafeIcon icon={FiCar} className="w-5 h-5 mr-2 text-primary-500" />
        AI Vehicle Description Generator
      </h3>
      
      <div className="bg-gray-50 p-3 rounded-lg mb-4">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <p className="text-gray-500">Vehicle:</p>
          <p className="font-medium">{vehicle.year} {vehicle.make} {vehicle.model}</p>
          
          <p className="text-gray-500">Type:</p>
          <p className="font-medium">{vehicle.type}</p>
          
          <p className="text-gray-500">Color:</p>
          <p className="font-medium">{vehicle.color}</p>
          
          <p className="text-gray-500">Price:</p>
          <p className="font-medium">${vehicle.price?.toLocaleString()}</p>
        </div>
      </div>
      
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={generateDescription}
        disabled={isLoading}
        className="w-full bg-primary-600 text-white py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 mb-4 disabled:opacity-50"
      >
        {isLoading ? (
          <>
            <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
            <span>Generating...</span>
          </>
        ) : (
          <>
            <SafeIcon icon={FiCar} className="w-4 h-4" />
            <span>Generate Sales Description</span>
          </>
        )}
      </motion.button>
      
      {description && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 relative">
          <p className="text-sm whitespace-pre-wrap">{description}</p>
          <button
            onClick={copyToClipboard}
            className="absolute top-2 right-2 p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-full transition-colors"
            title="Copy to clipboard"
          >
            <SafeIcon icon={copied ? FiCheck : FiCopy} className={`w-4 h-4 ${copied ? 'text-green-500' : ''}`} />
          </button>
        </div>
      )}
    </div>
  );
}

export default VehicleDescriptionGenerator;